import { eurFmt } from "@/lib/constants";

export const INVOICE_STATUS = {
  POR_PAGAR: { label: "Por pagar", className: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  EM_ATRASO: { label: "Em atraso", className: "bg-red-500/15 text-red-600 dark:text-red-400" },
  PAGA: { label: "Paga", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
};

export const TEMPLATE_CATEGORIES = {
  ENVIO: "Envio de fatura",
  LEMBRETE: "Lembrete",
  ATRASO: "Em atraso",
  AGRADECIMENTO: "Agradecimento",
  GERAL: "Geral",
};

export const PLACEHOLDERS = [
  { key: "cliente", label: "Nome do cliente" },
  { key: "contacto", label: "Nome do contacto" },
  { key: "fatura", label: "Nº da fatura" },
  { key: "valor", label: "Valor" },
  { key: "emissao", label: "Data de emissão" },
  { key: "vencimento", label: "Data de vencimento" },
  { key: "dias_atraso", label: "Dias em atraso" },
  { key: "mes", label: "Mês a que se refere" },
  { key: "total_em_divida", label: "Total em dívida do cliente" },
];

const MONTHS = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];

export function fmtDatePt(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

export function monthLabel(period) {
  if (!period) return "";
  const [y, m] = period.split("-");
  const name = MONTHS[Number(m) - 1];
  return name ? `${name} de ${y}` : period;
}

export function todayIso() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

/** Emails dos contactos do cliente, com nome, para escolher o destinatário. */
export function clientRecipients(client) {
  return (client?.contacts || [])
    .filter((c) => c && typeof c === "object" && c.email)
    .map((c) => ({ name: c.name || "", email: c.email.trim() }));
}

export function buildContext({ client, invoice, recipient, allInvoices = [], period }) {
  const unpaid = allInvoices.filter((i) => i.clientId === client?.id && i.status !== "PAGA");
  const firstName = (recipient?.name || "").trim().split(/\s+/)[0];
  return {
    cliente: client?.name || invoice?.clientName || "",
    contacto: firstName || client?.name || "",
    fatura: invoice?.number || "",
    valor: invoice ? eurFmt.format(invoice.amount || 0) : "",
    emissao: fmtDatePt(invoice?.issueDate),
    vencimento: fmtDatePt(invoice?.dueDate),
    dias_atraso: invoice?.daysOverdue ? String(invoice.daysOverdue) : "0",
    mes: monthLabel(invoice?.period || period || todayIso().slice(0, 7)),
    total_em_divida: eurFmt.format(unpaid.reduce((sum, i) => sum + (i.amount || 0), 0)),
  };
}

export function fillTemplate(text, context) {
  return (text || "").replace(/\{(\w+)\}/g, (match, key) => (context[key] !== undefined && context[key] !== "" ? context[key] : match));
}

export function mailtoHref({ to, subject, body }) {
  return `mailto:${encodeURIComponent(to || "")}?subject=${encodeURIComponent(subject || "")}&body=${encodeURIComponent(body || "")}`;
}

export function suggestedCategory(invoice) {
  if (!invoice) return "ENVIO";
  if (invoice.status === "PAGA") return "AGRADECIMENTO";
  if (invoice.status === "EM_ATRASO") return "ATRASO";
  if (invoice.emailCount > 0) return "LEMBRETE";
  return "ENVIO";
}

// ----------------------------- CSV -----------------------------
/** Lê CSV com `;` ou `,`, aspas e quebras de linha dentro de campos. */
export function parseCsv(text) {
  const clean = text.replace(/^﻿/, "");
  const firstLine = clean.split(/\r?\n/)[0] || "";
  const counts = { ";": (firstLine.match(/;/g) || []).length, ",": (firstLine.match(/,/g) || []).length, "\t": (firstLine.match(/\t/g) || []).length };
  const delimiter = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < clean.length; i += 1) {
    const ch = clean[i];
    if (quoted) {
      if (ch === '"' && clean[i + 1] === '"') { field += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === delimiter) { row.push(field); field = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && clean[i + 1] === "\n") i += 1;
      row.push(field); field = "";
      if (row.some((cell) => cell.trim() !== "")) rows.push(row);
      row = [];
    } else field += ch;
  }
  row.push(field);
  if (row.some((cell) => cell.trim() !== "")) rows.push(row);
  const [header = [], ...data] = rows;
  return { headers: header.map((h) => h.trim()), rows: data };
}

/** "1.234,56 €" / "1234.56" / "1 234,56" → 1234.56 */
export function parseAmount(value) {
  let s = String(value ?? "").replace(/[€\s ]/g, "").trim();
  if (!s) return 0;
  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  if (lastComma > lastDot) s = s.replace(/\./g, "").replace(",", ".");
  else s = s.replace(/,/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** "31/08/2026", "31-08-2026", "2026-08-31", "2026/08/31" → "2026-08-31" */
export function parseDate(value) {
  const s = String(value ?? "").trim();
  if (!s) return "";
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  m = s.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return "";
}

export function normalizeName(value) {
  return String(value ?? "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\b(lda|ltda|unipessoal|sa|s\.a\.)\b/g, "")
    .replace(/[^a-z0-9]/g, "");
}

export function matchClient(value, clients) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 9) {
    const byNif = clients.find((c) => (c.nif || "").replace(/\D/g, "") === digits);
    if (byNif) return byNif;
  }
  const target = normalizeName(raw);
  return clients.find((c) => normalizeName(c.name) === target)
    || clients.find((c) => target && normalizeName(c.name) && (normalizeName(c.name).includes(target) || target.includes(normalizeName(c.name))))
    || null;
}

/** Adivinha a coluna certa pelo nome do cabeçalho. */
export function guessColumn(headers, candidates) {
  const norm = headers.map((h) => normalizeName(h));
  for (const candidate of candidates) {
    const idx = norm.findIndex((h) => h.includes(normalizeName(candidate)));
    if (idx >= 0) return String(idx);
  }
  return "";
}
